import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { COUNTRIES } from '../utils/constants';

interface NationalityModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedNationality: string;
    onSelect: (country: string) => void;
    disabled?: boolean;
}

export default function NationalityModal({ 
    isOpen, 
    onClose, 
    selectedNationality, 
    onSelect, 
    disabled = false 
}: NationalityModalProps) {
    const { t } = useTranslation();

    // Close modal on Escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const getCountryDisplay = (country: string) => {
        if (country === "China") return "China (中国)";
        if (country === "Taiwan") return "Taiwan (台灣)";
        return country;
    };

    const handleSelect = (country: string) => {
        if (!disabled) {
            onSelect(country);
            onClose();
        }
    };

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div 
            className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center"
            onClick={handleBackdropClick}
            role="dialog"
            aria-modal="true"
            aria-labelledby="nationality-modal-title"
        >
            {/* Modal Content - Dropdown Style */}
            <div 
                className="relative bg-black border border-[var(--color-light-gray)] rounded-lg shadow-lg w-full max-w-md mx-8 max-h-[50vh] overflow-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="sr-only" id="nationality-modal-title">
                    {t('registration.nationalityPlaceholder')}
                </div>
                
                {COUNTRIES.map((country) => (
                    <div
                        key={country}
                        className={`px-4 py-2 text-center cursor-pointer hover:bg-gray-800 text-white ${
                            selectedNationality === country ? 'bg-blue-900' : ''
                        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onClick={() => handleSelect(country)}
                    >
                        {getCountryDisplay(country)}
                    </div>
                ))}
            </div>
        </div>
    );
} 